/* eslint-disable max-lines */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import _ from 'lodash';
import { useParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Divider } from '@material-ui/core';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import GetAppIcon from '@material-ui/icons/GetApp';
import ListIcon from '@material-ui/icons/List';
import ItemList from '@this/src/domain/approve_item/item_list';
import type { ItemListArgs } from '@this/src/domain/approve_item/item_list';
import {
  OrganizationBody,
  OrganizationTitle,
  OrganizationNavBar,
  OrganizationTable,
  OrganizationTh,
  OrganizationButtonTh,
  OrganizationTd,
  OrganizationTdButton
} from '@this/components/organization/organization.style';
import { Button } from '@this/src/components/shared/ui/inputs/button';
import { Loading } from '@this/src/components/shared/ui/feedbacks/loading';
import { Link } from '@this/src/components/shared/ui/navigations/link';
import { Input } from '@this/src/components/shared/ui/inputs/input';
import { Radio } from '@this/src/components/shared/ui/inputs/radio';
import { Checkbox } from '@this/src/components/shared/ui/inputs/checkbox';
import { Alert } from '@this/src/components/shared/ui/feedbacks/alert';
import { Modal, ModalHeader, ModalBody } from '@this/src/components/shared/ui/feedbacks/modal';
import { Pagination } from '@this/src/components/shared/ui/navigations/pagination';
import styled from 'styled-components';
import { ButtonType } from '@this/shared/atoms/button';
import { Fetcher, HTTPError, StrictFetcher } from '@this/src/util';
import type UserJson from '@this/domain/user/user_json';
import ApproveItemListForm from './approve_item_list_form';

type IndexResponse = {
  approve_item_lists: ItemListArgs[];
  current_page: number;
  total_pages: number;
  default_enabled: boolean;
  default_approve_item_list_id: number | null;
};

type CsvDownloadResponse = {
  action_id: number;
  message: string;
};

type CsvDownloadListResponse = {
  csv_downloads: Array<{
    id: number;
    status: string;
    status_str: string;
    user_name: string;
    created_at: string;
    finished_at: string | null;
    duration: number | null;
    downloadable: boolean;
    download_urls: { utf8: string; sjis: string } | null;
  }>;
};

const ApproveItemLists = observer(() => {
  const { approveItemId } = useParams<{ approveItemId: string }>();
  const [lists, setLists] = useState<ItemList[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchWord, setSearchWord] = useState<string>('');
  const [searchValidity, setSearchValidity] = useState<'valid' | 'invalid'>('valid');
  const [editingList, setEditingList] = useState<ItemList | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitErrors, setSubmitErrors] = useState<string[]>([]);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [defaultEnabled, setDefaultEnabled] = useState<boolean>(false);
  const [defaultListId, setDefaultListId] = useState<number | null>(null);
  const [defaultError, setDefaultError] = useState<string | null>(null);
  const [csvDownloads, setCsvDownloads] = useState<CsvDownloadListResponse['csv_downloads']>([]);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showHistoryModal, setShowHistoryModal] = useState<boolean>(false);
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);
  const [userEmails, setUserEmails] = useState<string>('');
  const [csvDownloadDisabled, setCsvDownloadDisabled] = useState<boolean>(false);
  const [refreshLoading, setRefreshLoading] = useState(false);

  const fetchData = useCallback(
    async (searchPage: number = page) => {
      setLoading(true);
      try {
        const params = {
          word: searchWord,
          validity: searchValidity,
          page: searchPage
        };
        const response = await Fetcher.get<IndexResponse>(
          `/organization/approve_items/${approveItemId}/approve_item_lists.json`,
          params
        );
        const itemLists = response.approve_item_lists.map(l => new ItemList(l));
        setLists(itemLists);
        setPage(response.current_page);
        setTotalPages(response.total_pages);
        setDefaultEnabled(response.default_enabled);
        setDefaultListId(response.default_approve_item_list_id);
      } catch (e) {
        setError('通信環境が不安定です。\n時間をおいてもう一度お試しください。');
      } finally {
        setLoading(false);
      }
    },
    [approveItemId, searchWord, searchValidity, page]
  );

  useEffect(() => {
    fetchData();
    fetchCsvDownloads();
    fetchUserEmails();
  }, []);

  const handleClickEdit = (list: ItemList) => {
    const tmpList = _.cloneDeep(list);
    setEditingList(tmpList);
  };

  const handleClickAdd = () => {
    setEditingList(new ItemList({} as any));
  };

  const handleClickDestroy = (list: ItemList) => {
    if (confirm('無効化しますか？')) {
      Fetcher.delete(`/organization/approve_items/${approveItemId}/approve_item_lists/${list.id}`).then(() => {
        fetchData();
      });
    }
  };

  const handleClickEnable = (list: ItemList) => {
    if (confirm('有効化しますか？')) {
      Fetcher.put(`/organization/approve_items/${approveItemId}/approve_item_lists/${list.id}/enable`, {}).then(
        () => {
          fetchData();
        }
      );
    }
  };

  const handleCloseModal = () => {
    setSubmitErrors([]);
    setEditingList(null);
  };

  const handleClickSubmit = () => {
    if (!editingList) return;
    if (editingList.id) {
      updateItemList(editingList);
    } else {
      createItemList(editingList);
    }
  };

  const updateItemList = async (itemList: ItemList) => {
    setSubmitting(true);
    setSubmitErrors([]);
    try {
      await Fetcher.put(
        `/organization/approve_items/${approveItemId}/approve_item_lists/${itemList.id}`,
        itemList.submitParams
      );
      setEditingList(null);
      fetchData();
    } catch (e) {
      if (e instanceof HTTPError && e.response?.status === 400 && e.response.data.errors) {
        const errors = e.response.data.errors;
        setSubmitErrors(errors);
      } else {
        setSubmitErrors(['通信環境が不安定です。\n時間をおいてもう一度お試しください。']);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const createItemList = async (itemList: ItemList) => {
    setSubmitting(true);
    setSubmitErrors([]);
    try {
      await Fetcher.post(`/organization/approve_items/${approveItemId}/approve_item_lists`, itemList.submitParams);
      setEditingList(null);
      fetchData();
    } catch (e) {
      if (e instanceof HTTPError && e.response?.status === 400 && e.response.data.errors) {
        const errors = e.response.data.errors;
        setSubmitErrors(errors);
      } else {
        setSubmitErrors(['通信環境が不安定です。\n時間をおいてもう一度お試しください。']);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClickSearch = () => {
    fetchData(1);
  };

  const submitDefaultSetting = async (
    enabled: boolean,
    listId: number | null,
    prev: { enabled: boolean; listId: number | null }
  ) => {
    setDefaultError(null);
    try {
      await Fetcher.put(`/organization/approve_items/${approveItemId}/approve_item_lists/default_setting`, {
        default_enabled: enabled,
        default_approve_item_list_id: listId
      });
    } catch (e) {
      setDefaultEnabled(prev.enabled);
      setDefaultListId(prev.listId);
      setDefaultError('初期値設定の保存に失敗しました。\n時間をおいてもう一度お試しください。');
    }
  };

  const handleToggleDefaultEnabled = (e: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = e.target.checked;
    const prev = { enabled: defaultEnabled, listId: defaultListId };
    const nextListId = enabled ? defaultListId : null;
    setDefaultEnabled(enabled);
    setDefaultListId(nextListId);
    submitDefaultSetting(enabled, nextListId, prev);
  };

  const handleSelectDefault = (list: ItemList) => {
    const prev = { enabled: defaultEnabled, listId: defaultListId };
    setDefaultEnabled(true);
    setDefaultListId(list.id);
    submitDefaultSetting(true, list.id, prev);
  };

  const fetchCsvDownloads = useCallback(async () => {
    setRefreshLoading(true);
    try {
      const response = await Fetcher.get<CsvDownloadListResponse>('/organization/approve_items/csv_downloads');
      setCsvDownloads(response.csv_downloads);
    } catch (e) {
      console.error('ダウンロードデータ一覧の取得に失敗しました:', e);
      // eslint-disable-next-line no-alert
      alert('ダウンロードデータ一覧取得に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setRefreshLoading(false);
    }
  }, []);

  const fetchUserEmails = useCallback(async () => {
    try {
      const response = await StrictFetcher.get<UserJson>('/users.json', 'users/index.json');
      const emails = [response.email, ...response.user_forwarded_emails.map(e => e.email)]
        .filter(Boolean)
        .join(', ');
      setUserEmails(emails);
    } catch (e) {
      console.error('ユーザーメール情報の取得に失敗しました:', e);
      setUserEmails('メールアドレス取得エラー');
    }
  }, []);

  const handleStartCsvDownload = () => {
    setShowConfirmModal(true);
  };

  const handleCsvDownload = () => {
    setShowHistoryModal(true);
    fetchCsvDownloads();
  };

  const CSV_STATUS_CHECK_INTERVAL = 10000; // 10秒
  const CSV_STATUS_MAX_RETRIES = 6; // 最大6回（1分間）
  const CSV_STATUS_ERROR_MAX_RETRIES = 3; // エラー時の最大リトライ回数

  const checkCsvStatus = useCallback(async (actionId: number, retryCount = 0) => {
    try {
      const response = await Fetcher.get<CsvDownloadListResponse>('/organization/approve_items/csv_downloads');
      const latestAction = response.csv_downloads.find(download => download.id === actionId);

      if (latestAction && (latestAction.status === 'succeeded' || latestAction.status === 'failed')) {
        setCsvDownloadDisabled(false);
        return;
      }

      if (retryCount < CSV_STATUS_MAX_RETRIES) {
        setTimeout(() => checkCsvStatus(actionId, retryCount + 1), CSV_STATUS_CHECK_INTERVAL);
      } else {
        setCsvDownloadDisabled(false);
      }
    } catch (e) {
      console.error('CSV処理状況の確認に失敗しました:', e);
      if (retryCount < CSV_STATUS_ERROR_MAX_RETRIES) {
        setTimeout(() => checkCsvStatus(actionId, retryCount + 1), CSV_STATUS_CHECK_INTERVAL);
      } else {
        setCsvDownloadDisabled(false);
      }
    }
  }, []);

  const handleCreateCsvDownload = async () => {
    setDownloadLoading(true);
    try {
      const response = await Fetcher.post<CsvDownloadResponse>('/organization/approve_items/csv_downloads', {
        approve_item_id: approveItemId
      });
      setShowConfirmModal(false);
      setCsvDownloadDisabled(true);
      checkCsvStatus(response.action_id);
      fetchCsvDownloads();
    } catch (e) {
      if (e instanceof HTTPError && e.response?.data?.error) {
        console.error(e.response.data.error);
      } else {
        console.error('CSVダウンロード準備に失敗しました。時間をおいて再度お試しください。');
      }
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleDirectDownload = (downloadUrl: string) => {
    window.location.href = downloadUrl;
  };

  const columnCount = defaultEnabled ? 5 : 4;
  const styles = useMemo(() => getStyles(defaultEnabled), [defaultEnabled]);

  return (
    <>
      <OrganizationNavBar>
        <Link
          href={`/organization/approve_items/${approveItemId}/edit`}
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <ChevronLeft />
          戻る
        </Link>
      </OrganizationNavBar>
      <OrganizationTitle>リスト申請項目</OrganizationTitle>
      <OrganizationBody>
        <Wrap>
          <div style={{ marginBottom: '20px' }}>
            <p style={{ fontSize: '1.1em', fontWeight: 'bold' }}>検索条件</p>
            <SearchInputs>
              <Input value={searchWord} onChange={e => setSearchWord(e.target.value)} placeholder="検索ワード" />
              <div style={{ display: 'flex', alignItems: 'center', columnGap: '5px' }}>
                <Radio
                  name="searchValidity"
                  value="valid"
                  checked={searchValidity === 'valid'}
                  onChange={() => setSearchValidity('valid')}
                >
                  有効
                </Radio>
                <Radio
                  name="searchValidity"
                  value="invalid"
                  checked={searchValidity === 'invalid'}
                  onChange={() => setSearchValidity('invalid')}
                >
                  無効
                </Radio>
              </div>
              <Button onClick={handleClickSearch}>検索</Button>
            </SearchInputs>
          </div>
          <Divider />
          <div
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '20px 0' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', columnGap: '10px' }}>
              <Button color="sub" onClick={handleClickAdd} disabled={!!editingList}>
                <span style={{ marginRight: '5px' }}>項目を追加</span>
                <AddCircleOutlineIcon />
              </Button>
              <Button color="sub" onClick={handleStartCsvDownload} disabled={!!editingList || csvDownloadDisabled}>
                <span style={{ marginRight: '5px' }}>CSVダウンロード</span>
                <GetAppIcon />
              </Button>
              {csvDownloads.length > 0 && (
                <Button color="sub" onClick={handleCsvDownload} disabled={!!editingList}>
                  <span style={{ marginRight: '5px' }}>ダウンロードデータ一覧</span>
                  <ListIcon />
                </Button>
              )}
            </div>
            <Button
              color="sub"
              href={`/organization/approve_items/${approveItemId}/approve_item_lists/bulk_upsert`}
              disabled={!!editingList}
            >
              リスト項目一括追加・更新
            </Button>
          </div>
          {!loading && (
            <div style={styles.defaultSettingBar}>
              <Checkbox checked={defaultEnabled} onChange={handleToggleDefaultEnabled}>
                初期値を設定する
              </Checkbox>
              <span style={styles.defaultSettingNote}>
                {defaultEnabled ? '・下記より初期表示を設定できます' : '・ONにすると初期値を設定できます'}
              </span>
            </div>
          )}
          {defaultError && (
            <Alert variant="danger" onClose={() => setDefaultError(null)} style={styles.alert}>
              {defaultError}
            </Alert>
          )}
          <OrganizationTable>
            <thead>
              <tr>
                <OrganizationTh>項目コード</OrganizationTh>
                <OrganizationTh>項目名</OrganizationTh>
                {defaultEnabled && <OrganizationButtonTh>初期値</OrganizationButtonTh>}
                <OrganizationButtonTh>編集</OrganizationButtonTh>
                <OrganizationButtonTh>無効化/有効化</OrganizationButtonTh>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columnCount}>
                    <Loading />
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={columnCount}>
                    <div className="error">{error}</div>
                  </td>
                </tr>
              ) : (
                lists.map(list => (
                  <tr key={list.id}>
                    <OrganizationTd>{list.code}</OrganizationTd>
                    <OrganizationTd>{list.name}</OrganizationTd>
                    {defaultEnabled && (
                      <OrganizationTd>
                        {!list.deletedAt && (
                          <Radio
                            name="defaultApproveItemList"
                            checked={defaultListId === list.id}
                            onChange={() => handleSelectDefault(list)}
                          >
                            {''}
                          </Radio>
                        )}
                      </OrganizationTd>
                    )}
                    <OrganizationTd>
                      {!list.deletedAt && (
                        <OrganizationTdButton onClick={e => handleClickEdit(list)}>編集</OrganizationTdButton>
                      )}
                    </OrganizationTd>
                    <OrganizationTd>
                      {list.deletedAt ? (
                        <OrganizationTdButton onClick={() => handleClickEnable(list)}>有効化</OrganizationTdButton>
                      ) : (
                        <OrganizationTdButton
                          buttonType={ButtonType.DANGER}
                          onClick={() => {
                            handleClickDestroy(list);
                          }}
                        >
                          無効化
                        </OrganizationTdButton>
                      )}
                    </OrganizationTd>
                  </tr>
                ))
              )}
            </tbody>
          </OrganizationTable>
          <Pagination
            page={page}
            count={totalPages}
            onChange={(e, p) => {
              fetchData(p);
            }}
          />
        </Wrap>
        {editingList && (
          <Modal open={!!editingList} onClose={handleCloseModal}>
            <ModalHeader>{editingList.id ? '項目を変更する' : '項目を追加する'}</ModalHeader>
            <ModalBody>
              <ApproveItemListForm approveItemList={editingList} />
              {!!submitErrors.length && (
                <div className="error">
                  {submitErrors.map(e => (
                    <p key={e}>{e}</p>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', columnGap: '5px' }}>
                <Button color="sub" onClick={handleCloseModal} disabled={submitting}>
                  キャンセル
                </Button>
                <Button color="primary" onClick={handleClickSubmit} disabled={submitting}>
                  保存
                </Button>
              </div>
            </ModalBody>
          </Modal>
        )}
        {showConfirmModal && (
          <Modal open={showConfirmModal} onClose={() => setShowConfirmModal(false)}>
            <ModalHeader>CSVダウンロード確認</ModalHeader>
            <ModalBody>
              <div>
                <p>{userEmails}にCSVをメールで送信します。</p>
                <p>UTF-8とShift_JIS両方の形式のファイルを送信します。</p>
                <p>ファイルの送信が完了するまで、次のダウンロードはできません。</p>
                <div style={{ display: 'flex', justifyContent: 'flex-end', columnGap: '10px', marginTop: '20px' }}>
                  <Button color="sub" onClick={() => setShowConfirmModal(false)} disabled={downloadLoading}>
                    キャンセル
                  </Button>
                  <Button color="primary" onClick={handleCreateCsvDownload} disabled={downloadLoading}>
                    {downloadLoading ? '準備中...' : '確認'}
                  </Button>
                </div>
              </div>
            </ModalBody>
          </Modal>
        )}
        {showHistoryModal && (
          <Modal open={showHistoryModal} onClose={() => setShowHistoryModal(false)}>
            <ModalHeader>帳票出力一覧</ModalHeader>
            <ModalBody>
              <div>
                <p>帳票の出力に時間がかかる場合があります。</p>
                <p>「最新の出力状況を取得」ボタンを押すことで、最新の出力状況を取得できます。</p>
                <div style={{ display: 'flex', justifyContent: 'center', margin: '15px 0' }}>
                  <Button color="primary" onClick={fetchCsvDownloads} disabled={refreshLoading}>
                    {refreshLoading ? '取得中...' : '最新の出力状況を取得'}
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: '20px' }}>
                {csvDownloads.length === 0 ? (
                  <p>履歴がありません</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ backgroundColor: '#f5f5f5' }}>
                      <tr>
                        <th style={{ padding: '12px 8px', textAlign: 'left', border: '1px solid #ddd' }}>
                          出力日
                        </th>
                        <th style={{ padding: '12px 8px', textAlign: 'left', border: '1px solid #ddd' }}>状態</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left', border: '1px solid #ddd' }}>
                          ダウンロード
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvDownloads.map(download => (
                        <tr key={download.id}>
                          <td style={{ padding: '12px 8px', border: '1px solid #ddd' }}>{download.created_at}</td>
                          <td style={{ padding: '12px 8px', border: '1px solid #ddd' }}>
                            <span
                              style={{
                                color: download.downloadable ? '#28a745' : '#ffc107',
                                fontWeight: 'bold'
                              }}
                            >
                              {download.status_str}
                            </span>
                          </td>
                          <td style={{ padding: '12px 8px', border: '1px solid #ddd' }}>
                            {download.downloadable && download.download_urls ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <Button
                                  color="primary"
                                  size="small"
                                  onClick={() => handleDirectDownload(download.download_urls!.utf8)}
                                >
                                  UTF-8
                                </Button>
                                <Button
                                  color="primary"
                                  size="small"
                                  onClick={() => handleDirectDownload(download.download_urls!.sjis)}
                                >
                                  Shift_JIS
                                </Button>
                              </div>
                            ) : (
                              <span style={{ color: '#6c757d' }}>準備中</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                <Button color="sub" onClick={() => setShowHistoryModal(false)}>
                  閉じる
                </Button>
              </div>
            </ModalBody>
          </Modal>
        )}
      </OrganizationBody>
    </>
  );
});

const Wrap = styled.div`
  padding: 10px;
`;

const SearchInputs = styled.div`
  display: flex;
  align-items: center;
  column-gap: 20px;
`;

function getStyles(defaultEnabled: boolean) {
  return {
    defaultSettingBar: {
      display: 'flex',
      alignItems: 'center',
      columnGap: '8px',
      padding: '12px',
      marginBottom: '16px',
      backgroundColor: defaultEnabled ? '#eaf3fb' : 'transparent',
      borderRadius: '4px',
      border: defaultEnabled ? '1px solid #CFE2F1' : '1px dashed #C7CDD3'
    } as const,
    defaultSettingNote: {
      fontSize: '13px'
    } as const,
    alert: {
      marginBottom: '16px'
    } as const
  };
}

export default ApproveItemLists;
